import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { MsalAuthenticationTemplate, MsalProvider } from '@azure/msal-react';
import { useDispatch, useSelector } from 'react-redux';
import {clearEntraIdAuthMode} from '../authMode';
import { ICON_DANGER } from '../../core/consts';
import { ENTRA_ID_SCOPES, EntraIdClient, getEntraIdClient } from './EntraIdClient';
import {EntraIdValidatedConfiguration, readEntraIdConfiguration} from './EntraIdConfiguration';
import {EntraIdConfigurationError} from './EntraIdErrors';
import {entraIdAuthFailed, entraIdConfigurationFailed, entraIdInitializationStarted, entraIdRedirectStarted} from '../../core/actions/entraIdAuthActions';
import {ENTRA_ID_SESSION_STATUS} from '../../reducers/entraIdAuth';
import type {EntraIdAuthRootState} from '../../reducers/entraIdAuth';
import DialogInfo from '../../comp/feedback/DialogInfo';
import Loading from '../../comp/form/loading';
import Resource from '../../core/serverresource';
import EntraIdTokenHandler from './EntraIdTokenHandler';


type EntraIdAuthGateProps = PropsWithChildren<{}>;
type EntraIdConfigurationResult =
    | {config: EntraIdValidatedConfiguration; error?: undefined}
    | {config?: undefined; error: EntraIdConfigurationError};

export const retryEntraAuthentication = async (
    client: Pick<EntraIdClient, 'clearCachedAccounts'>,
    reload: () => void = () => window.location.reload(),
): Promise<void> => {
    await client.clearCachedAccounts();
    reload();
};

export const EntraIdAuthGate = ({ children }: EntraIdAuthGateProps): JSX.Element => {
    const [pca, setPca] = useState<PublicClientApplication | undefined>(undefined);
    const dispatch = useDispatch();
    const sessionStatus = useSelector((state: EntraIdAuthRootState) => (
        state.entraIdSession?.status ?? ENTRA_ID_SESSION_STATUS.INITIALIZING
    ));

    const configurationResult: EntraIdConfigurationResult = useMemo(() => {
        try {
            return {config: readEntraIdConfiguration(Resource)};
        } catch (error) {
            return {error: error as EntraIdConfigurationError};
        }
    }, []);
    const client = useMemo(
        () => configurationResult.config ? getEntraIdClient(configurationResult.config) : undefined,
        [configurationResult],
    );

    /** Keeps the initialization effect stable until its client or Redux dispatch dependency changes. */
    const initializeAuth = useCallback(async (): Promise<void> => {
        if (!client) {
            return;
        }
        try {
            dispatch(entraIdInitializationStarted());
            const msalInstance = await client.initialize();
            setPca(msalInstance);
        } catch (error) {
            console.error('MSAL initialization failed:', error);
            dispatch(entraIdAuthFailed());
        }
    }, [client, dispatch]);

    useEffect(() => {
        document.documentElement.lang = 'cs';
        if (configurationResult.error) {
            console.error('Invalid Entra ID configuration:', configurationResult.error);
            dispatch(entraIdConfigurationFailed());
            return;
        }
        initializeAuth();
    }, [configurationResult, dispatch, initializeAuth]);

    useEffect(() => {
        if (client && sessionStatus === ENTRA_ID_SESSION_STATUS.REAUTHENTICATION_REQUIRED) {
            dispatch(entraIdRedirectStarted());
            client.startInteractiveLogin().catch((error) => {
                console.error('MSAL interactive login failed:', error);
                dispatch(entraIdAuthFailed());
            });
        }
    }, [client, dispatch, sessionStatus]);
    const retryAuth = async (): Promise<void> => {
        if (!client) {
            return;
        }
        try {
            // Do not call fireLogout(): its sessionStorage clear would lose the persisted Entra mode.
            await retryEntraAuthentication(client);
        } catch (error) {
            console.error('MSAL cache clear before retry failed:', error);
        }
    };

    const logoutAuth = async (): Promise<void> => {
        if (!client) {
            return;
        }
        try {
            clearEntraIdAuthMode({
                storage: window.sessionStorage,
                url: new URL(window.location.href),
                history: window.history,
            });
            await client.logout();
        } catch (error) {
            console.error('MSAL logout failed:', error);
        }
    };

    if (sessionStatus === ENTRA_ID_SESSION_STATUS.FAILED) {
        return (
            <DialogInfo
                color="danger"
                isOpen={true}
                icon={ICON_DANGER}
                onSubmit={retryAuth}
                onToggle={logoutAuth}
                header="Je nám líto, ale přihlášení selhalo"
                info="Během vašeho přihlášení došlo k problému. Vyčištěním přihlašovacích dat můžete přihlášení zkusit znovu."
                submitLabel="Vyčistit data a obnovit stránku"
                toggleLabel="Odhlásit se"
            />
        );
    }

    if (sessionStatus === ENTRA_ID_SESSION_STATUS.CONFIGURATION_FAILED) {
        return (
            <DialogInfo
                color="danger"
                isOpen={true}
                icon={ICON_DANGER}
                onSubmit={undefined}
                onToggle={undefined}
                header="Konfigurace Entra ID není platná"
                info="Konfigurace přihlášení k Entra ID v této aplikaci není platná. Kontaktujte prosím podporu aplikace."
                submitLabel=""
            />
        );
    }

    if (!pca || !client || !configurationResult.config) {
        return <Loading text={undefined} />;
    }

    return (
        <MsalProvider instance={pca}>
            <MsalAuthenticationTemplate
                interactionType={InteractionType.Redirect}
                authenticationRequest={{
                    scopes: ENTRA_ID_SCOPES,
                    prompt: 'select_account',
                }}
            >
                <EntraIdTokenHandler client={client} apiConfiguration={configurationResult.config}>
                    {children}
                </EntraIdTokenHandler>
            </MsalAuthenticationTemplate>
        </MsalProvider>
    );
};

export default EntraIdAuthGate;
